import { OIDC } from './oidc-config'

/**
 * GET the portal SSO session endpoint with cookies. The portal returns 200 + token data when the user has a
 * valid rh_oidc_session cookie, 401 otherwise.
 *
 * @returns {Promise<Response>}
 */
export function fetchSession() {
  return fetch(OIDC.sessionCheckUrl, { credentials: 'include' })
}

/**
 * Writes the portal /session or /exchange response into localStorage. Objects are stringified so localStorage
 * never ends up with "[object Object]". A missing user is dropped instead of stored, because
 * JSON.stringify(undefined) returns the value undefined, which localStorage coerces to the literal string
 * "undefined" and breaks a later JSON.parse.
 *
 * @param {Object} result Parsed JSON body from /auth-oidc/session or /auth-oidc/exchange.
 * @returns {boolean} True if a token was applied; false if the body has no usable token.
 */
export function applySessionResult(result) {
  if (!result || !result.data || !result.data.token) return false
  // permissionsId drives loggedIn(), which compares localStorage.permissionsId > 40. A missing or non-numeric
  // value would be stored as the string "undefined", so loggedIn() returns false while a token is present - which
  // dead-ends requireAuth in a redirect loop (login -> SSO probe sees the token -> won't re-redirect -> login).
  // Treat a non-numeric permissionsId as an unusable session.
  if (!Number.isFinite(Number(result.data.permissionsId))) return false
  const user = result.data.user
  if (user == null) {
    delete localStorage.user
  } else {
    localStorage.user = typeof user === 'string' ? user : JSON.stringify(user)
  }
  localStorage.token = result.data.token
  localStorage.role = result.data.role
  localStorage.permissionsId = result.data.permissionsId
  return true
}

/**
 * Removes all auth-related keys from localStorage. Used by logout and by the visibility-driven revalidation
 * when /auth-oidc/session returns 401.
 *
 * @returns {void}
 */
export function clearLocalAuthState() {
  delete localStorage.user
  delete localStorage.token
  delete localStorage.role
  delete localStorage.permissionsId
  delete localStorage.activeSite
}

/**
 * Writes an OIDC error into sessionStorage under the `oidc_error` key. LoginPage reads and clears it on mount
 * to show a toast on the next bootstrap.
 *
 * @param {string} code Short error code (e.g. `'exchange_failed'`, `'invalid_request'`).
 * @param {string} [description] Optional human-readable detail for the console; the user-facing toast uses
 *   a generic translation key.
 * @returns {void}
 */
export function saveOidcError(code, description) {
  sessionStorage.setItem('oidc_error', JSON.stringify({
    code,
    description: description || '',
  }))
}

/**
 * Writes the OIDC reason flag into sessionStorage under the `oidc_reason` key. Used by the post-logout return
 * path: the portal forwards `?reason=expired` in the URL search, but Vue Router (hash mode) can't see search
 * params, so the bootstrap moves it into sessionStorage where LoginPage can read it.
 *
 * @param {string} reason Currently only `'expired'` is meaningful.
 * @returns {void}
 */
export function saveOidcReason(reason) {
  sessionStorage.setItem('oidc_reason', reason)
}

// The registration code being redeemed. openOidcLogin() remembers it when an invitation login starts and forgets
// it on every other login, the bootstrap routes an OIDC error back to the invitation page while it is set, and it
// is cleared after a successful exchange or when the invitation page has shown the error.
export const INVITATION_CODE_KEY = 'oidc_invitation_code'

export function saveInvitationCode(code) {
  sessionStorage.setItem(INVITATION_CODE_KEY, code)
}

export function readInvitationCode() {
  return sessionStorage.getItem(INVITATION_CODE_KEY)
}

export function clearInvitationCode() {
  sessionStorage.removeItem(INVITATION_CODE_KEY)
}

/**
 * Route the app boots to after an OIDC error: the invitation page while an invitation is being redeemed (it
 * shows the dedicated message), otherwise the login page.
 *
 * @returns {string}
 */
export function errorBootTarget() {
  const code = readInvitationCode()
  return code ? '/invitation/' + encodeURIComponent(code) : '/login'
}

// Invitation outcomes get their own text; every other OIDC code gets the generic OIDC text (today the password
// wording is shown for every OIDC failure).
const DEDICATED_ERROR_KEYS = {
  invitation_invalid: 'login-page.oidc.invitation-invalid',
  invitation_email_mismatch: 'login-page.oidc.invitation-email-mismatch',
  email_in_use: 'login-page.oidc.email-in-use',
}

/**
 * Translation key for a stored OIDC error (the JSON written by saveOidcError).
 *
 * @param {string|null} errorRaw
 * @returns {string}
 */
export function oidcErrorMessageKey(errorRaw) {
  let code = null
  try {
    code = JSON.parse(errorRaw).code
  } catch (e) {
    code = null
  }
  return DEDICATED_ERROR_KEYS[code] || 'login-page.oidc.error'
}
